import { socket } from '@/api/socket';
import { SOCKET_EVENTS } from '@/api/socket-events';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { IClient, IPhone } from '@/entities/client.entity';
import { cn } from '@/lib/utils';
import { formatPhoneDisplay } from '@/lib/format-phone';
import { groupUssdPresetsByCategory, resolveUssdOperatorPresets } from '@crave/config/ussd-presets';
import { useMemo, useState, type FormEvent } from 'react';
import { toast } from 'sonner';

function formatPhoneSubtitle(phone: IPhone): string {
	const parts: string[] = [];
	if (phone.simSlotIndex != null) parts.push(`slot ${phone.simSlotIndex}`);
	if (phone.countryIso) parts.push(phone.countryIso.toUpperCase());
	if (phone.isDefault) parts.push('основная');
	if (phone.numberResolveReason === 'awaiting_sms') parts.push('ожидание SMS');
	if (phone.number) parts.push(formatPhoneDisplay(phone.number));
	return parts.join(' · ') || 'Без номера';
}

function isPhoneInFlight(phone: IPhone): boolean {
	return phone.status === 1 || phone.status === 2;
}

function getDefaultPhoneId(phones: IPhone[], initialPhoneId?: number | null): number | undefined {
	if (initialPhoneId != null && phones.some(p => p.id === initialPhoneId)) return initialPhoneId;
	return phones[0]?.id;
}

function SendUssdForm({
	phones,
	initialPhoneId,
	onClose,
}: {
	phones: IPhone[];
	initialPhoneId?: number | null;
	onClose: () => void;
}) {
	const defaultPhoneId = getDefaultPhoneId(phones, initialPhoneId);
	const defaultPhone = phones.find(p => p.id === defaultPhoneId);
	const [phoneId, setPhoneId] = useState(() => String(defaultPhoneId ?? ''));
	const [ussdText, setUssdText] = useState(() => resolveUssdOperatorPresets(defaultPhone?.name)[0]?.command ?? '');

	const selectedPhone = phones.find(p => String(p.id) === phoneId);
	const presets = useMemo(() => resolveUssdOperatorPresets(selectedPhone?.name), [selectedPhone?.name]);
	const presetGroups = useMemo(() => groupUssdPresetsByCategory(presets), [presets]);
	const inFlight = selectedPhone ? isPhoneInFlight(selectedPhone) : false;
	const showAsyncSmsHint = useMemo(() => {
		const trimmed = ussdText.trim();
		const preset = presets.find(p => p.command === trimmed);
		return preset?.asyncDelivery === 'sms';
	}, [presets, ussdText]);

	function handlePhoneChange(id: number) {
		setPhoneId(String(id));
		const phone = phones.find(p => p.id === id);
		setUssdText(resolveUssdOperatorPresets(phone?.name)[0]?.command ?? '');
	}

	function handleSubmit(e: FormEvent) {
		e.preventDefault();
		const trimmed = ussdText.trim();
		if (!trimmed || !phoneId) return toast.error('Выберите SIM и введите USSD-код');

		socket.emit(SOCKET_EVENTS.createActionUssd, { phoneId: Number(phoneId), ussdText: trimmed });
		onClose();
	}

	return (
		<form onSubmit={handleSubmit} className='flex flex-col min-h-0'>
			<DialogHeader className='px-4 pt-4 pb-2 shrink-0 text-left'>
				<DialogTitle>USSD-запрос</DialogTitle>
				{selectedPhone ? (
					<p className='text-sm text-muted-foreground font-normal'>
						{selectedPhone.name}
						<span className='mx-1'>·</span>
						{formatPhoneSubtitle(selectedPhone)}
					</p>
				) : null}
			</DialogHeader>

			<div className='px-4 pb-2 shrink-0'>
				<span className='text-xs font-medium text-muted-foreground uppercase tracking-wide'>SIM</span>
				<div className='mt-1.5 flex flex-wrap gap-1.5'>
					{phones.map(phone => {
						const active = String(phone.id) === phoneId;
						return (
							<button
								key={phone.id}
								type='button'
								disabled={!phones.length}
								className={cn(
									'rounded-lg border px-2.5 py-1.5 text-left text-xs transition-colors',
									active
										? 'border-primary bg-accent text-foreground'
										: 'border-border bg-muted/30 text-muted-foreground hover:bg-accent/60 hover:text-foreground',
								)}
								onClick={() => handlePhoneChange(phone.id)}
							>
								<span className='block font-medium'>{phone.name}</span>
								<span className='block opacity-80 tabular-nums'>
									{phone.number
										? formatPhoneDisplay(phone.number)
										: phone.status === 1
											? 'ожидание'
											: 'без номера'}
								</span>
							</button>
						);
					})}
				</div>
			</div>

			<div className='px-4 pb-2 shrink-0'>
				{presets.length ? (
					<>
						<span className='text-xs font-medium text-muted-foreground uppercase tracking-wide'>Полезные USSD</span>
						<div className='mt-1.5 max-h-[40vh] overflow-y-auto space-y-3 pr-1'>
							{presetGroups.map(group => (
								<div key={group.category}>
									<span className='text-[10px] font-medium text-muted-foreground uppercase tracking-wide'>
										{group.label}
									</span>
									<div className='mt-1 flex flex-wrap gap-1.5'>
										{group.presets.map(preset => {
											const selected = ussdText === preset.command;
											return (
												<button
													key={preset.command}
													type='button'
													className={cn(
														'rounded-lg border px-2.5 py-1.5 text-left transition-colors min-w-[88px]',
														selected
															? 'border-primary bg-accent'
															: 'border-border bg-muted/30 hover:bg-accent/60',
													)}
													onClick={() => setUssdText(preset.command)}
												>
													<span className='block font-mono text-sm'>{preset.command}</span>
													<span className='block text-[10px] text-muted-foreground leading-tight mt-0.5'>
														{preset.label}
													</span>
												</button>
											);
										})}
									</div>
								</div>
							))}
						</div>
					</>
				) : selectedPhone ? (
					<p className='text-xs text-muted-foreground'>
						Для оператора «{selectedPhone.name}» пресеты не найдены — введите код вручную.
					</p>
				) : null}
			</div>

			<div className='px-4 pb-2 shrink-0'>
				<Label className='text-xs font-medium text-muted-foreground uppercase tracking-wide'>Свой код</Label>
				<Input
					className='font-mono mt-1.5'
					value={ussdText}
					onChange={e => setUssdText(e.target.value)}
					placeholder='*100*4#'
					autoComplete='off'
				/>
				{inFlight ? (
					<p className='text-xs text-muted-foreground mt-2'>
						Предыдущая USSD-команда может ещё выполняться на устройстве. Повторный запрос будет отправлен как новая
						команда.
					</p>
				) : null}
				{showAsyncSmsHint ? (
					<p className='text-xs text-amber-600 dark:text-amber-500 mt-2'>
						Beeline: ответ на этот USSD придёт в SMS (0674), не в вкладке USSD.
					</p>
				) : null}
			</div>

			<div className='px-4 pb-4 shrink-0 mt-auto'>
				<Button type='submit' className='w-full' disabled={!ussdText.trim() || !phoneId || !phones.length}>
					Отправить
				</Button>
			</div>
		</form>
	);
}

export default function SendUssdModal({
	client,
	open,
	onOpenChange,
	initialPhoneId,
}: {
	client: IClient;
	open?: boolean;
	onOpenChange?: (open: boolean) => void;
	initialPhoneId?: number | null;
}) {
	const phones = useMemo(() => client.phones?.filter(p => p?.id != null) ?? [], [client.phones]);
	const [internalOpen, setInternalOpen] = useState(false);
	const isControlled = open !== undefined;
	const isOpen = isControlled ? open : internalOpen;
	const formKey = isOpen ? String(getDefaultPhoneId(phones, initialPhoneId) ?? 'empty') : 'closed';

	function setOpen(value: boolean) {
		if (!isControlled) setInternalOpen(value);
		onOpenChange?.(value);
	}

	return (
		<Dialog open={isOpen} onOpenChange={setOpen}>
			<DialogContent className='sm:max-w-[440px] w-full p-0 gap-0 overflow-hidden'>
				{isOpen ? (
					<SendUssdForm key={formKey} phones={phones} initialPhoneId={initialPhoneId} onClose={() => setOpen(false)} />
				) : null}
			</DialogContent>
		</Dialog>
	);
}
